// flag-adjust.ts — Lane INV2 S1 / S1b (2026-09-27): the client-truth INVENTORY FLAG resolver + commit queue.
//
// Wares and Hidden are bits; Inspection DC is an integer on the inventory row.
// The browser decides an explicit desired value — never "invert" — and commits it to
// options/game/actions/inventory_flag_truth_commit_inc.php (schema janus.inventory.flag.v1), which proves authority from the
// target row, CASes the row's holder + flag, and patches the caches instead of fanning out a recompile.
//
// Commits for ONE row are serialized here (a per-row queue): each request's `before` is the value the previous request left
// confirmed, so rapid clicks (1 → 0 → 1) land in order and a slow earlier answer can never overwrite a later intent. A desired
// value equal to the confirmed one sends nothing.
import { inventoryField, itemsCol } from '../../shared/array-types.js';
import { statusRows, statusItemId } from '../statuses/expiry.js';
import type { InventoryHiddenPlan } from './hidden-plan.js';
import { inventoryFlagValueRefusal, INVENTORY_FLAG_SCHEMA } from './flag-value-contract.js';

export { INVENTORY_FLAG_SCHEMA };
export const INVENTORY_FLAG_ENDPOINT = '/options/game/actions/inventory_flag_truth_commit_inc.php';
export type InventoryFlagKind = 'wares' | 'hidden' | 'inspection-dc';
export type Bit = 0 | 1;

export interface InventoryFlagCommand {
  schema: typeof INVENTORY_FLAG_SCHEMA;
  operation_id: string;
  encounter_id: string;
  inventory_id: string;
  kind: InventoryFlagKind;
  actor_id: string | null;
  before: { value: number; possess_id: string };
  after: { value: number };
  hidden_plan?: InventoryHiddenPlan;
}
export interface InventoryFlagSaved { possess_id: string; value: number }
export class InventoryFlagPreparationError extends Error {
  constructor(reason: string, public readonly saved: InventoryFlagSaved) { super(reason); }
}
export interface InventoryFlagAnswer {
  ok: boolean;
  /** HTTP status; 0 = never answered (network). */
  status: number;
  applied?: boolean;
  /** The row's saved flag (a success: the committed value; a 409: SQL truth, null when the row left / was deleted). */
  saved?: InventoryFlagSaved | null;
  error?: string;
  /** True when nothing was sent (the desired value was already confirmed). */
  noop?: boolean;
  /** Local admission is complete; the lazy save reports corrections separately. */
  delivery?: Promise<InventoryFlagAnswer>;
}

type Row = Record<string, unknown>;
const asRow = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;
const upper = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const bit = (v: unknown): Bit => (Number(v) === 1 ? 1 : 0);

/** The `hidden` status on a compiled inventory row (object-card inventoryIsHidden twin: statuses.target, by item id). */
export function readInventoryHidden(row: Row | null | undefined, hiddenStatusId: string): Bit {
  const want = upper(hiddenStatusId);
  if (!want || !row) return 0;
  return statusRows(row, 'target').some(status => (statusItemId(status) || upper(itemsCol(status, 'item_id'))) === want) ? 1 : 0;
}
/** The wares flag on a compiled inventory row (build_inventory_arr emits it only when truthy). */
export function readInventoryWares(row: Row | null | undefined): Bit {
  return bit(asRow(row?.inventory)?.inventory_wares ?? row?.inventory_wares);
}

/**
 * TS twin of ft_inc_inventory_flag_patch_tree(): set `inventory_wares` on every compiled copy of the row inside `node` (a caster
 * blob's inventory tree, an encounter member's inventory list) — the key at 1, absent at 0. Mutates in place; returns the hits.
 */
export function applyWaresToTree(node: unknown, inventoryId: string, value: number, depth = 0): number {
  if (depth > 24) return 0;
  const want = upper(inventoryId);
  let hits = 0;
  if (Array.isArray(node)) {
    for (const child of node) hits += applyWaresToTree(child, want, value, depth + 1);
    return hits;
  }
  const obj = asRow(node);
  if (!obj) return 0;
  const bucket = asRow(obj.inventory);
  if (bucket && upper(bucket.inventory_id) === want) {
    if (value === 1) bucket.inventory_wares = 1; else delete bucket.inventory_wares;
    hits++;
  }
  for (const [key, child] of Object.entries(obj)) {
    if (key === 'inventory' && !Array.isArray(child)) continue;
    if (child !== null && typeof child === 'object') hits += applyWaresToTree(child, want, value, depth + 1);
  }
  return hits;
}

/** The first compiled copy of an inventory row inside `root` (a caster blob, an encounter), or null. */
export function findInventoryRow(root: unknown, inventoryId: string, depth = 0): Row | null {
  if (depth > 24 || root === null || typeof root !== 'object') return null;
  const want = upper(inventoryId);
  if (!want) return null;
  if (!Array.isArray(root)) {
    if (upper(inventoryField(root, 'inventory_id')) === want) return root as Row;
  }
  for (const child of Array.isArray(root) ? root : Object.values(root as Row)) {
    const hit = findInventoryRow(child, want, depth + 1);
    if (hit) return hit;
  }
  return null;
}

const newOperationId = (): string => (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
  ? crypto.randomUUID()
  : `${Date.now().toString(16)}-0000-4000-8000-${Math.random().toString(16).slice(2, 14).padEnd(12, '0')}`).toUpperCase();

/** Build one command; null when desired === current (nothing to send). */
export function buildInventoryFlagCommand(input: {
  kind: InventoryFlagKind; encounterId: string; inventoryId: string; actorId?: string | null; possessId: string; current: number; desired: number; operationId?: string;
}): InventoryFlagCommand | null {
  if (input.current === input.desired) return null;
  return {
    schema: INVENTORY_FLAG_SCHEMA, operation_id: input.operationId ?? newOperationId(), encounter_id: upper(input.encounterId),
    inventory_id: upper(input.inventoryId), kind: input.kind, actor_id: input.kind === 'wares' ? upper(input.actorId) : null,
    before: { value: input.current, possess_id: upper(input.possessId) }, after: { value: input.desired },
  };
}

/** POST one command; retries the SAME operation on no answer / 5xx (the server's receipt makes that safe). */
export async function postInventoryFlag(command: InventoryFlagCommand, fetchImpl: typeof fetch = fetch): Promise<InventoryFlagAnswer> {
  const refusal = inventoryFlagValueRefusal(command);
  if (refusal) return { ok: false, status: 0, error: refusal };
  let last: InventoryFlagAnswer = { ok: false, status: 0, error: 'not sent' };
  for (let attempt = 0; attempt < 3; attempt++) {
    let response: Response;
    try {
      response = await fetchImpl(INVENTORY_FLAG_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(command) });
    } catch (error) {
      last = { ok: false, status: 0, error: error instanceof Error ? error.message : String(error) };
      continue;
    }
    const body = asRow(await response.json().catch(() => null));
    const savedRow = asRow(body?.saved);
    const saved: InventoryFlagSaved | null | undefined = body && 'saved' in body
      ? (savedRow ? { possess_id: upper(savedRow.possess_id), value: command.kind === 'inspection-dc' ? Number(savedRow.value) : bit(savedRow.value) } : null) : undefined;
    last = { ok: response.ok && body?.ok === true, status: response.status, applied: body?.applied === true, saved, error: body?.ok === true ? undefined : String(body?.error ?? response.statusText) };
    if (last.ok || (response.status >= 400 && response.status < 500)) return last;
  }
  return last;
}

/** Per-row queue: the confirmed value the next request starts from, and the tail of the in-flight chain. */
const confirmed = new Map<string, { value: number; possessId: string }>();
const tails = new Map<string, Promise<unknown>>();
const projected = new Map<string, { value: number; possessId: string; operationId: string }>();
const queueKey = (kind: InventoryFlagKind, inventoryId: string): string => kind + ':' + upper(inventoryId);

export function pendingInventoryFlag(kind: InventoryFlagKind, inventoryId: string): Readonly<{ value: number; operationId: string }> | undefined {
  return projected.get(queueKey(kind, inventoryId));
}

/** The value this page last saw CONFIRMED for the row (seeded by the first commit from what the page showed). */
export function confirmedInventoryFlag(kind: InventoryFlagKind, inventoryId: string): { value: number; possessId: string } | null {
  return confirmed.get(queueKey(kind, inventoryId)) ?? null;
}

/**
 * Queue an explicit desired value for one row. `seed` is what the page shows as server truth right now (read before any
 * optimistic paint); it is used only when no earlier commit of this row confirmed a value. Resolves with the answer (or a
 * noop answer when the row already holds `desired`). A 409 adopts the saved value as the new confirmed floor.
 */
export function queueInventoryFlag(input: {
  kind: InventoryFlagKind; encounterId: string; inventoryId: string; actorId?: string | null; desired: number; seed: { value: number; possessId: string };
  onPrepared?: () => void;
}, fetchImpl: typeof fetch = fetch, prepareHidden?: (command: InventoryFlagCommand) => Promise<InventoryHiddenPlan>,
  enqueueSave?: (command: InventoryFlagCommand) => Promise<InventoryFlagAnswer>): Promise<InventoryFlagAnswer> {
  const key = queueKey(input.kind, input.inventoryId);
  const run = async (): Promise<InventoryFlagAnswer> => {
    // The floor is read when THIS request runs (Codex review INV2 S1 #8): a refusal or an ambiguous answer earlier in the chain
    // may have dropped it, and then this request's own seed (what its page showed) is the best known truth.
    const base = projected.get(key) ?? confirmed.get(key) ?? { value: input.seed.value, possessId: upper(input.seed.possessId) };
    const command = buildInventoryFlagCommand({ kind: input.kind, encounterId: input.encounterId, inventoryId: input.inventoryId, actorId: input.actorId,
      possessId: base.possessId, current: base.value, desired: input.desired });
    if (!command) {
      confirmed.set(key, base);
      input.onPrepared?.();
      return { ok: true, status: 200, noop: true, saved: { possess_id: base.possessId, value: base.value } };
    }
    if (input.kind === 'hidden') {
      try {
        if (!prepareHidden) throw new Error('inventory-hidden:planner-not-loaded');
        command.hidden_plan = await prepareHidden(command);
      } catch (error) {
        if (error instanceof InventoryFlagPreparationError) {
          confirmed.set(key, { value: error.saved.value, possessId: error.saved.possess_id });
          return { ok: false, status: 409, error: error.message, saved: error.saved };
        }
        return { ok: false, status: 0, error: error instanceof Error ? error.message : String(error) };
      }
    }
    const refusal = inventoryFlagValueRefusal(command);
    if (refusal) return { ok: false, status: 0, error: refusal };
    input.onPrepared?.();
    const reconcile = (answer: InventoryFlagAnswer): InventoryFlagAnswer => {
      if (projected.get(key)?.operationId === command.operation_id) projected.delete(key);
      if (answer.ok) confirmed.set(key, { value: answer.saved?.value ?? input.desired, possessId: answer.saved?.possess_id ?? base.possessId });
      else if (answer.saved) confirmed.set(key, { value: answer.saved.value, possessId: answer.saved.possess_id });
      else confirmed.delete(key);
      return answer;
    };
    if (enqueueSave) {
      projected.set(key, { value: input.desired, possessId: base.possessId, operationId: command.operation_id });
      return { ok: true, status: 200, applied: true, delivery: enqueueSave(command).then(reconcile) };
    }
    return reconcile(await postInventoryFlag(command, fetchImpl));
  };
  const next = (tails.get(key) ?? Promise.resolve()).then(run, run);
  const tail = next.catch(() => undefined);
  tails.set(key, tail);
  // Settled tails only leave the map, and only when no newer request replaced them (review #10: never evict an active chain).
  void tail.then(() => { if (tails.get(key) === tail) tails.delete(key); });
  return next;
}

/** A frame another client committed: this page's confirmed floor for the row moves with it. */
export function noteInventoryFlagFrame(kind: InventoryFlagKind, inventoryId: string, saved: InventoryFlagSaved | null): void {
  const key = queueKey(kind, inventoryId);
  if (!saved) { confirmed.delete(key); return; }
  confirmed.set(key, { value: saved.value, possessId: upper(saved.possess_id) });
}
